/**
 * A role's sections: building them for a role that has none, editing them in
 * the form, and comparing two sets of them.
 *
 * The form shows every role the same way: key details, then the job
 * description section by section (FR-47). A role read from a document has its
 * own sections. Any other role has its sections built from its fields here.
 */
import type { BulkRow, DocSection, JobDescription } from '../types'

/**
 * The fields a role keeps for pricing, under the headings a person reads them
 * by, in the order a job description puts them (FR-52).
 *
 * **A copy of `backend/src/lib/sections.ts`**, because the browser cannot
 * import server code. Change both. A test runs the two over the same roles,
 * including the shapes they could read differently.
 */
export const BUILT_HEADINGS = [
  ['summary', 'Summary'],
  ['responsibilities', 'Responsibilities'],
  ['skills', 'Required skills'],
  ['quals', 'Qualifications'],
  ['physicalDemands', 'Physical demands'],
  ['workEnvironment', 'Work environment'],
  ['totalRewards', 'Total rewards'],
  ['compensationNote', 'Compensation note'],
  ['legal', 'Legal'],
] as const

/** The fields `builtSections` reads. Every other part of a role is a key detail. */
export type HiddenText = Partial<Pick<JobDescription, (typeof BUILT_HEADINGS)[number][0]>>

/** One field as lines: a list item per line, a paragraph split where it breaks. */
const linesOf = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value])
    .flatMap((item) => (typeof item === 'string' ? item.split(/\r?\n/) : []))
    .map((line) => line.trim())
    .filter((line) => line !== '')

/** Sections built from a role's fields, only where it has text. Mirrors the server. */
export function builtSections(role: HiddenText): DocSection[] {
  const out: DocSection[] = []
  for (const [field, heading] of BUILT_HEADINGS) {
    const lines = linesOf(role[field])
    if (lines.length) out.push({ heading, lines })
  }
  return out
}

/** What the form shows: the role's own sections, or ones built from its fields. */
export function sectionsOf(role: HiddenText & { sections?: DocSection[] }): DocSection[] {
  return role.sections?.length ? role.sections : builtSections(role)
}

/**
 * A mass upload row's text as a role holds it (FR-61).
 *
 * **A copy of `rowText` in `backend/src/lib/sections.ts`.** Change both. A
 * pipe separates the items of a column. A spreadsheet may also separate
 * skills with a comma; a skill read out of a document is a whole sentence and
 * is never split on one.
 */
export function rowText(row: Pick<BulkRow, 'summary' | 'responsibilities' | 'skills' | 'quals' | 'from'>) {
  const split = (v: string, on: string | RegExp = '|') =>
    v.split(on).map((x) => x.trim()).filter(Boolean)
  return {
    summary: row.summary,
    responsibilities: split(row.responsibilities),
    skills: row.from === 'model' ? split(row.skills) : split(row.skills, /[|,]/),
    quals: split(row.quals),
  }
}

/** What the Edit panel shows for a mass upload row: its sections, or ones built from its columns. */
export const rowSections = (row: BulkRow): DocSection[] =>
  sectionsOf({ ...rowText(row), sections: row.sections })

/** The sections a new role typed by hand starts with (FR-53). */
export const STARTING_HEADINGS = ['Summary', 'Responsibilities', 'Required skills', 'Qualifications']

/* ---------- the form ---------- */

/**
 * One section as the form edits it: the heading, and the lines as one block
 * of text, one line per item. The key is the form's own, so React keeps a
 * box's focus and cursor when sections are moved around it.
 */
export interface EditableSection {
  key: string
  heading: string
  text: string
}

let made = 0
const nextKey = () => `s${++made}`

export const toEditable = (sections: DocSection[]): EditableSection[] =>
  sections.map((s) => ({ key: nextKey(), heading: s.heading, text: s.lines.join('\n') }))

/**
 * The form's sections, as the role stores them.
 *
 * Each line as typed, with only its outer spaces taken off; an empty line is
 * not a line. A block with neither a heading nor a line says nothing and is
 * dropped. The server cleans them again the same way an upload is cleaned.
 */
export const fromEditable = (sections: EditableSection[]): DocSection[] =>
  sections
    .map((s) => ({
      heading: s.heading.trim(),
      lines: s.text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l !== ''),
    }))
    .filter((s) => s.heading !== '' || s.lines.length > 0)

export const blankSection = (heading = ''): EditableSection => ({ key: nextKey(), heading, text: '' })

/** Moves the section at `from` one place up (-1) or down (+1). Out of range does nothing. */
export function moveSection<T>(sections: T[], from: number, by: -1 | 1): T[] {
  const to = from + by
  if (from < 0 || from >= sections.length || to < 0 || to >= sections.length) return sections
  const out = [...sections]
  const [moved] = out.splice(from, 1)
  out.splice(to, 0, moved!)
  return out
}

/* ---------- comparing ---------- */

/** Spaces tidied and empty lines dropped, as the server cleans them before comparing. */
const tidy = (sections: DocSection[]): DocSection[] =>
  sections
    .map((s) => ({
      heading: s.heading.replace(/\s+/g, ' ').trim(),
      lines: s.lines.map((l) => l.replace(/\s+/g, ' ').trim()).filter((l) => l !== ''),
    }))
    .filter((s) => s.heading !== '' || s.lines.length > 0)

/** Whether two sets of sections say the same thing. */
export const sameSections = (a: DocSection[], b: DocSection[]): boolean =>
  JSON.stringify(tidy(a)) === JSON.stringify(tidy(b))

/* ---------- the document's title, kept off the screen (FR-68) ---------- */

/**
 * The lines a document uses as its own title when it is not the role's name.
 * Some documents start with one, and the reader takes it for a section with
 * nothing under it, so the role opened on an empty box (STE-986).
 */
const TITLE_LINES = [
  'job description', 'position description', 'role description',
  'job profile', 'role profile', 'position profile', 'job specification',
]

/** Words only: case, spacing and punctuation aside. */
const wordsOf = (text: string) => text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()

/** Whether a heading only repeats the document's title: the role's name, a title line, or both. */
function isTitle(heading: string, title: string): boolean {
  const h = wordsOf(heading)
  if (!h) return false
  const t = wordsOf(title)
  if (t && h === t) return true
  return TITLE_LINES.some((line) => h === line || (t !== '' && (h === `${line} ${t}` || h === `${t} ${line}`)))
}

/**
 * How many sections at the top of a role only repeat the document's title,
 * with nothing under them. An empty heading with any other name, or one
 * further down, may be a real sub-heading and is not counted.
 */
export function titleLead(sections: DocSection[], title: string): number {
  let n = 0
  while (n < sections.length) {
    const s = sections[n]!
    if (s.lines.some((line) => line.trim() !== '') || !isTitle(s.heading, title)) break
    n++
  }
  return n
}

/**
 * The sections as the screen shows them: without that title at the top.
 *
 * **The screen only.** What is stored, and what the hidden fields were read
 * from, keep it, so Benchmarking and Match a job read what they always read.
 */
export const shownSections = (sections: DocSection[], title: string): DocSection[] =>
  sections.slice(titleLead(sections, title))

/** One difference between the current sections and the proposed ones (FR-19). */
export type SectionChange =
  | { kind: 'added'; heading: string; lines: string[] }
  | { kind: 'removed'; heading: string; lines: string[] }
  | { kind: 'renamed'; from: string; to: string; before: string[]; after: string[] }
  | { kind: 'lines'; heading: string; before: string[]; after: string[] }
  | { kind: 'order'; before: string[]; after: string[] }

const same = (a: string[], b: string[]) => a.join('\n') === b.join('\n')

/**
 * The sections that changed, one entry each, so HR reads what moved rather
 * than hunting through two copies of the role (FR-19).
 *
 * A section is followed by its heading first. A proposed section whose heading
 * is new is a rename when it stands where an unmatched current section stood,
 * and an addition otherwise. A current section nothing matched was removed.
 * If the sections both sides share are in a different order, that is said
 * once, as the headings before and after.
 */
export function sectionChanges(current: DocSection[], proposed: DocSection[]): SectionChange[] {
  const before = tidy(current)
  const after = tidy(proposed)
  const used = new Set<number>()
  const pairs: (number | null)[] = after.map((s) => {
    const at = before.findIndex((b, i) => !used.has(i) && b.heading === s.heading)
    if (at < 0) return null
    used.add(at)
    return at
  })
  // A new heading where an unmatched section stood is the same section renamed.
  after.forEach((_, i) => {
    if (pairs[i] !== null) return
    if (i < before.length && !used.has(i)) {
      pairs[i] = i
      used.add(i)
    }
  })

  const out: SectionChange[] = []
  after.forEach((s, i) => {
    const at = pairs[i]
    if (at === null || at === undefined) {
      out.push({ kind: 'added', heading: s.heading, lines: s.lines })
      return
    }
    const was = before[at]!
    if (was.heading !== s.heading) {
      out.push({ kind: 'renamed', from: was.heading, to: s.heading, before: was.lines, after: s.lines })
    } else if (!same(was.lines, s.lines)) {
      out.push({ kind: 'lines', heading: s.heading, before: was.lines, after: s.lines })
    }
  })
  before.forEach((s, i) => {
    if (!used.has(i)) out.push({ kind: 'removed', heading: s.heading, lines: s.lines })
  })

  // The order of what both sides still share.
  const kept = pairs.filter((p): p is number => p !== null)
  const sorted = [...kept].sort((a, b) => a - b)
  if (kept.some((p, i) => p !== sorted[i])) {
    out.push({
      kind: 'order',
      before: sorted.map((i) => before[i]!.heading),
      after: pairs.map((p, i) => (p === null ? null : after[i]!.heading)).filter((h): h is string => h !== null),
    })
  }
  return out
}
